/**
 * Lightweight password strength heuristic.
 *
 * Not zxcvbn — but enough to nudge users away from "password123456" while
 * still rewarding length over symbol-soup. Length is the dominant factor;
 * character-class diversity is a tiebreaker. We deliberately do NOT enforce
 * "must contain a number / symbol" — that's the bad-old-NIST policy that
 * pushes users toward "Password1!" and away from passphrases.
 *
 * Scoring (0–4):
 *   0 weak       – under the 12-char minimum, or repetitive / sequential
 *   1 fair       – meets the minimum, low diversity
 *   2 good       – ≥14 chars or ≥12 with mixed character classes
 *   3 strong     – ≥16 chars or ≥14 with mixed character classes
 *   4 excellent  – ≥20 chars
 */
export type Strength = {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  hint: string;
};

const COMMON = new Set([
  "password",
  "passw0rd",
  "qwerty",
  "abc123",
  "letmein",
  "iloveyou",
  "111111",
  "admin",
  "welcome",
  "monkey",
  "dragon",
]);

function characterClasses(value: string): number {
  let classes = 0;
  if (/[a-z]/.test(value)) classes++;
  if (/[A-Z]/.test(value)) classes++;
  if (/\d/.test(value)) classes++;
  if (/[^A-Za-z0-9]/.test(value)) classes++;
  return classes;
}

function isRepetitive(value: string): boolean {
  // Same character ≥4 in a row, or ascending/descending sequence ≥4.
  if (/(.)\1{3,}/.test(value)) return true;
  for (let i = 0; i <= value.length - 4; i++) {
    const a = value.charCodeAt(i);
    const b = value.charCodeAt(i + 1);
    const c = value.charCodeAt(i + 2);
    const d = value.charCodeAt(i + 3);
    if (b - a === 1 && c - b === 1 && d - c === 1) return true;
    if (a - b === 1 && b - c === 1 && c - d === 1) return true;
  }
  return false;
}

export function scorePassword(value: string): Strength {
  if (!value) return { score: 0, label: "Weak", hint: "Use at least 12 characters." };

  const lower = value.toLowerCase();
  if (COMMON.has(lower)) {
    return { score: 0, label: "Too common", hint: "Pick something only you would think of." };
  }
  if (isRepetitive(value)) {
    return { score: 0, label: "Too predictable", hint: "Avoid repeating or sequential patterns." };
  }
  if (value.length < 12) {
    return {
      score: 0,
      label: "Weak",
      hint: `${12 - value.length} more character${12 - value.length === 1 ? "" : "s"} to go.`,
    };
  }

  const classes = characterClasses(value);
  const len = value.length;

  if (len >= 20)
    return { score: 4, label: "Excellent", hint: "A passphrase like this is hard to guess." };
  if (len >= 16 || (len >= 14 && classes >= 3))
    return { score: 3, label: "Strong", hint: "Looking good." };
  if (len >= 14 || (len >= 12 && classes >= 3))
    return { score: 2, label: "Good", hint: "Try a few more characters for extra strength." };
  return {
    score: 1,
    label: "Fair",
    hint: "Adding length is the easiest way to make it stronger.",
  };
}
